import type { PoliciesListProps } from './policies-list.interface';
import type { FC, ReactNode } from 'react';
import type { ConnectedProps } from 'react-redux';
import type { PolicyDto } from 'shared/dtos/policies.dto';

import {
  closestCenter,
  DndContext,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { bindActionCreators } from '@reduxjs/toolkit';
import { usePermissions } from 'contexts';
import { useState } from 'react';
import { connect } from 'react-redux';
import { updatePolicyThunk } from 'store/policies/policies.thunks';

import { EntityPanel } from '../../panels/entity-panel/entity-panel';

const SortableItem = ({
  id,
  children,
}: {
  id: number;
  children: ReactNode;
}) => {
  const matrix = usePermissions();
  const isDraggingDisabled =
    matrix.permissions.dragFields === 'empty' ||
    matrix.permissions.dragFields === false;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: isDraggingDisabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: isDragging ? 'grabbing' : 'grab',
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  );
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      updatePolicyThunk,
    },
    dispatch,
  );

const connector = connect(null, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export const PoliciesList: FC<PropsFromRedux & PoliciesListProps> = ({
  policies,
  onSelect,
  updatePolicyThunk,
}: PropsFromRedux & PoliciesListProps) => {
  const [policiesLocalState, setPoliciesLocalState] =
    useState<PolicyDto[]>(policies);

  const isTouchDevice =
    typeof window !== 'undefined' && 'ontouchstart' in window;
  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 8 },
  });

  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: { delay: 100, tolerance: 5 },
  });

  const sensors = useSensors(isTouchDevice ? touchSensor : pointerSensor);

  const onDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = policiesLocalState.findIndex(
        (item) => item.id === active.id,
      );

      const newIndex = policiesLocalState.findIndex(
        (item) => item.id === over.id,
      );

      setPoliciesLocalState((prevState) => {
        const newArr = arrayMove(prevState, oldIndex, newIndex).map(
          (policy, index) => ({ ...policy, order: index + 1 }),
        );
        return newArr;
      });

      const policyByIdx = policiesLocalState[oldIndex];

      const targetPolicy = {
        ...policyByIdx,
        Priorities: (policyByIdx.Priorities || []).map((priority) => ({
          ...priority,
          MidtermGoals: (priority.MidtermGoals || []).map((midtermGoal) => ({
            ...midtermGoal,
            Agencies: (midtermGoal.Agencies || []).map(
              (agency) => agency.id ?? agency,
            ),
          })),
        })),
      };

      updatePolicyThunk({
        ...targetPolicy,
        order: newIndex + 1,
        id: targetPolicy.id,
      });
    }
  };

  const itemsForContext = policiesLocalState.map((policy) => policy.id);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={onDragEnd}
    >
      <SortableContext
        items={itemsForContext}
        strategy={verticalListSortingStrategy}
      >
        {policiesLocalState.map((item) => (
          <SortableItem key={item.id} id={item.id}>
            <EntityPanel
              onClick={() => onSelect(item)}
              title={item.title}
              id={item.id}
            />
          </SortableItem>
        ))}
      </SortableContext>
    </DndContext>
  );
};

export default connector(PoliciesList);
